import { DefaultPrivacyLevel, datadogRum } from '@datadog/browser-rum-slim';
import { createElement, useRef, Fragment } from 'react';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { useLocation } from 'react-router';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function checkNotBot(datadogStore) {
    return !((datadogStore === null || datadogStore === void 0 ? void 0 : datadogStore.isBot) || false);
}
function checkCanTrack(appData) {
    var _a;
    return ((_a = appData.context) === null || _a === void 0 ? void 0 : _a.privacyTrackingState) === 'CAN_TRACK';
}
// For documentation, refer to README.md
function buildDataDogRumInitConfiguration(pluginOptions, appData, datadogStore) {
    return {
        // allowedTracingOrigins - Disabled for now until RUM + APM Tracing is enabled.
        // allowedTracingOrigins - A list of request origins used to inject tracing headers. For more information, see Connect RUM and Traces (https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum&tabs=browserrum)
        // allowedTracingOrigins: pluginOptions.allowedTracingOrigins,
        applicationId: pluginOptions.applicationId,
        clientToken: pluginOptions.clientToken,
        defaultPrivacyLevel: parseDefaultPrivacyLevelOption(pluginOptions),
        env: (datadogStore === null || datadogStore === void 0 ? void 0 : datadogStore.env) || 'unknown',
        premiumSampleRate: pluginOptions.premiumSampleRate,
        service: pluginOptions.service || appData.stores.page.appName,
        sampleRate: pluginOptions.sampleRate,
        site: pluginOptions.datadogSite,
        trackInteractions: pluginOptions.trackInteractions,
        trackViewsManually: pluginOptions.trackViewsManually,
        version: (datadogStore === null || datadogStore === void 0 ? void 0 : datadogStore.appVersion) || 'unknown',
        // beforeSend - Callback function that gives you access to every event collected by the RUM Browser SDK before it is sent to Datadog (https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tabs=npm)
        //              You can add additional context attributes to the event.
        //              For example, tag your RUM resource events with data extracted from a fetch response object
        //     event:   The event generated by the RUM Browser SDK
        //     context: The context that triggered the creation of the RUM event
        beforeSend: function (event, context) {
            var _a, _b, _c;
            event.context = __assign(__assign({}, event.context), { duaid: ((_b = (_a = appData.context) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.duaid) || '', 'trace-id': ((_c = appData.context) === null || _c === void 0 ? void 0 : _c.traceInfo['Trace-ID']) || '' });
        },
    };
}
function parseDefaultPrivacyLevelOption(pluginOptions) {
    switch (pluginOptions.defaultPrivacyLevel) {
        case 'allow':
            return DefaultPrivacyLevel.ALLOW;
        case 'mask':
            return DefaultPrivacyLevel.MASK;
        default:
            return DefaultPrivacyLevel.MASK_USER_INPUT;
    }
}

var DDRUM_CALLING_START_VIEW = new SystemEvent(SystemEventLevel.INFO, 'DDRUM_CALLING_START_VIEW');
var DDRUM_START_VIEW_NOT_FOUND = new SystemEvent(SystemEventLevel.WARN, 'DDRUM_START_VIEW_NOT_FOUND');
var DDRUM_NOT_FOUND = new SystemEvent(SystemEventLevel.WARN, 'DDRUM_NOT_FOUND');
// For documentation, refer to README.md
function DatadogRUMPlugin(pluginOptions) {
    return {
        name: 'bernie-plugin-datadog-rum',
        onBrowserHydrateRenderContext: function (context, next) {
            var _a, _b;
            var hydratedState = context.hydratedState, getAppData = context.getAppData;
            var appData = getAppData();
            var datadogStore = hydratedState.datadogStore;
            if (checkNotBot(datadogStore) &&
                pluginOptions.enabled &&
                typeof window !== 'undefined' &&
                checkCanTrack(appData)) {
                var initConfiguration = buildDataDogRumInitConfiguration(pluginOptions, appData, datadogStore);
                datadogRum.init(initConfiguration);
                datadogRum.addRumGlobalContext('brandId', (_b = (_a = appData.context) === null || _a === void 0 ? void 0 : _a.site) === null || _b === void 0 ? void 0 : _b.brand);
            }
            return next(context);
        },
        // If plugin is using trackViewsManually=true, we only want to track all interactions with the same partial URL, ignoring the query parameters (everything after ?).
        // I.e. we don’t want to create a new DD RUM view whenever the URL query parameters change as this results in a
        // huge number of views and dilutes the ability to track things happening on the same page.
        // To facilitate this, we only call startView whenever the location.pathname
        // changes — paying attention only to everything after the initial / but before any ? or # delimiters.
        // We do this in onBrowserWrapRootElement as we need to be inside a React provider so that we can access a Ref (via useRef) to store the old pathname for comparison.
        onBrowserWrapRootElement: function (pluginContext, next) {
            if (pluginOptions.enabled && pluginOptions.trackViewsManually) {
                var logger_1 = pluginContext.logger, getAppData = pluginContext.getAppData, setRootElement = pluginContext.setRootElement;
                if (!window.DD_RUM) {
                    logger_1.logEvent(DDRUM_NOT_FOUND, 'datadog-rum: window.DD_RUM not found. Datadog RUM SDK is not initialized and metrics will not be sent.');
                    return next(pluginContext);
                }
                var appData_1 = getAppData();
                var DatadogRumWrapper_1 = function (props) {
                    var pathname = useRef('');
                    var location = useLocation();
                    if (pathname.current !== location.pathname) {
                        var datadogRumObj = window.DD_RUM;
                        if (datadogRumObj === null || datadogRumObj === void 0 ? void 0 : datadogRumObj.startView) {
                            logger_1.logEvent(DDRUM_CALLING_START_VIEW, 'datadog-rum: Path has changed. Calling datadogRumObj.startView("' +
                                appData_1.stores.page.pageId +
                                '")...');
                            datadogRumObj.startView(appData_1.stores.page.pageId);
                            pathname.current = location.pathname;
                        }
                        else {
                            logger_1.logEvent(DDRUM_START_VIEW_NOT_FOUND, 'datadog-rum: window.DD_RUM.startView not found. trackViewsManually not supported.');
                        }
                    }
                    return createElement(Fragment, null, props.children);
                };
                setRootElement(function (RootElement) { return function (props) { return (createElement(DatadogRumWrapper_1, null,
                    createElement(RootElement, null, props.children))); }; });
            }
            return next(pluginContext);
        },
    };
}

export default DatadogRUMPlugin;
//# sourceMappingURL=browser.js.map
